import React, { useState, useEffect } from 'react';
import { api } from '../App';
import toast from 'react-hot-toast';

const EMAIL_VARS = ['{{name}}', '{{city}}', '{{address}}'];
const SMS_VARS = ['{{name}}', '{{city}}', '{{address}}'];
const STATUS_LABELS = {
  draft: { bg: 'bg-yellow-50 text-yellow-700', icon: 'fa-pen' },
  sent: { bg: 'bg-primary/10 text-primary', icon: 'fa-paper-plane' },
  simulated: { bg: 'bg-blue-50 text-blue-700', icon: 'fa-flask' },
};

export default function Campaigns() {
  const [activeTab, setActiveTab] = useState('email');
  const [campaigns, setCampaigns] = useState([]);
  const [smsCampaigns, setSmsCampaigns] = useState([]);
  const [dripStats, setDripStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showSmsCreate, setShowSmsCreate] = useState(false);
  const [sending, setSending] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    name: '', subject: '', body: '', target_statuses: ['new', 'contacted'],
    schedule_type: 'immediate', follow_up_count: 0, follow_up_interval_days: 3,
  });
  const [smsForm, setSmsForm] = useState({
    name: '', message: '', target_statuses: ['new', 'contacted'],
    target_min_score: 0, follow_up_count: 1, follow_up_interval_days: 3,
  });

  const fetchAll = async () => {
    try {
      const [cRes, sRes, dRes] = await Promise.all([
        api.get('/api/campaigns'),
        api.get('/api/sms-campaigns'),
        api.get('/api/drip/stats'),
      ]);
      setCampaigns(cRes.data.campaigns || []);
      setSmsCampaigns(sRes.data.campaigns || []);
      setDripStats(dRes.data);
      setLoading(false);
    } catch { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.subject || !form.body) { toast.error('All fields required'); return; }
    try {
      const res = await api.post('/api/campaigns', form);
      setCampaigns(prev => [res.data, ...prev]);
      setShowCreate(false);
      setForm({ name: '', subject: '', body: '', target_statuses: ['new', 'contacted'], schedule_type: 'immediate', follow_up_count: 0, follow_up_interval_days: 3 });
      toast.success('Email campaign created!');
    } catch { toast.error('Failed'); }
  };

  const handleSend = async (id) => {
    setSending(id);
    try {
      const res = await api.post(`/api/campaigns/${id}/send`);
      toast.success(res.data.message);
      fetchAll();
    } catch { toast.error('Send failed'); }
    setSending(null);
  };

  const handleSmsCreate = async (e) => {
    e.preventDefault();
    if (!smsForm.name || !smsForm.message) { toast.error('Name and message required'); return; }
    if (smsForm.message.length > 320) { toast.error('Message too long (max 320 chars)'); return; }
    try {
      const res = await api.post('/api/sms-campaigns', smsForm);
      setSmsCampaigns(prev => [res.data, ...prev]);
      setShowSmsCreate(false);
      setSmsForm({ name: '', message: '', target_statuses: ['new', 'contacted'], target_min_score: 0, follow_up_count: 1, follow_up_interval_days: 3 });
      toast.success('SMS campaign created!');
    } catch { toast.error('Failed to create'); }
  };

  const handleSmsSend = async (id) => {
    setSending(id + '_sms');
    try {
      const res = await api.post(`/api/sms-campaigns/${id}/send`);
      const d = res.data;
      if (d.requires_phone_number) {
        toast.error('Add TWILIO_PHONE_NUMBER to backend .env for real SMS', { duration: 5000 });
      } else {
        toast.success(d.message || 'SMS campaign sent!');
      }
      fetchAll();
    } catch { toast.error('Send failed'); }
    setSending(null);
  };

  const handleSmsDelete = async (id) => {
    try {
      await api.delete(`/api/sms-campaigns/${id}`);
      setSmsCampaigns(prev => prev.filter(c => c.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/api/campaigns/${id}`); setCampaigns(prev => prev.filter(c => c.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  const viewDetail = async (id) => {
    try {
      const res = await api.get(`/api/campaigns/${id}`);
      setDetail(res.data);
    } catch { toast.error('Failed to load details'); }
  };

  const toggleStatus = (status) => {
    setForm(prev => ({
      ...prev,
      target_statuses: prev.target_statuses.includes(status) ? prev.target_statuses.filter(s => s !== status) : [...prev.target_statuses, status]
    }));
  };
  const toggleSmsStatus = (status) => {
    setSmsForm(prev => ({
      ...prev,
      target_statuses: prev.target_statuses.includes(status) ? prev.target_statuses.filter(s => s !== status) : [...prev.target_statuses, status]
    }));
  };

  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { setShowCreate(false); setShowSmsCreate(false); setDetail(null); } };
    if (showCreate || showSmsCreate || detail) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showCreate, showSmsCreate, detail]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary tracking-tight">Campaigns</h1>
          <p className="text-text-muted text-sm mt-1">Email & SMS outreach with automated drip sequences</p>
        </div>
        <button
          onClick={() => activeTab === 'sms' ? setShowSmsCreate(true) : setShowCreate(true)}
          className="btn-primary text-xs py-2.5"
          data-testid="create-campaign-btn"
        >
          <i className="fa-solid fa-plus mr-1.5" />New {activeTab === 'sms' ? 'SMS' : 'Email'} Campaign
        </button>
      </div>

      {/* Drip Stats Banner */}
      {dripStats && dripStats.total > 0 && (
        <div className="card-interactive p-4 border-primary/20 bg-primary/5 flex flex-wrap gap-5 items-center">
          <div className="flex items-center gap-2"><i className="fa-solid fa-water text-primary text-sm" /><span className="text-xs font-bold text-text-primary">Drip Engine</span></div>
          {[
            { l: 'Pending', v: dripStats.pending, c: 'text-orange-600' },
            { l: 'Sent', v: dripStats.sent, c: 'text-green-600' },
            { l: 'Simulated', v: dripStats.simulated, c: 'text-blue-600' },
            { l: 'Failed', v: dripStats.failed, c: 'text-destructive' },
          ].map(s => (
            <div key={s.l} className="flex items-center gap-1.5">
              <span className={`text-sm font-bold tabular-nums ${s.c}`}>{s.v}</span>
              <span className="text-xs text-text-muted">{s.l}</span>
            </div>
          ))}
          <span className="text-[10px] text-text-muted ml-auto">Processes every hour</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-surface-alt border border-border w-fit">
        <button onClick={() => setActiveTab('email')} className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${activeTab === 'email' ? 'bg-primary text-white shadow-sm' : 'text-text-muted hover:text-text-primary'}`} data-testid="email-tab">
          <i className="fa-solid fa-envelope" />Email
          {campaigns.length > 0 && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-white/20">{campaigns.length}</span>}
        </button>
        <button onClick={() => setActiveTab('sms')} className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 ${activeTab === 'sms' ? 'bg-green-600 text-white shadow-sm' : 'text-text-muted hover:text-text-primary'}`} data-testid="sms-tab">
          <i className="fa-solid fa-comment-sms" />SMS
          {smsCampaigns.length > 0 && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-white/20">{smsCampaigns.length}</span>}
        </button>
      </div>

      {/* ── EMAIL TAB ── */}
      {activeTab === 'email' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { l: 'Total Campaigns', v: campaigns.length, icon: 'fa-bullhorn', color: 'primary' },
              { l: 'Emails Sent', v: campaigns.reduce((a, c) => a + (c.sent_count || 0), 0), icon: 'fa-paper-plane', color: 'accent' },
              { l: 'Active', v: campaigns.filter(c => c.status === 'sent' || c.status === 'simulated').length, icon: 'fa-circle-check', color: 'primary' },
              { l: 'Drafts', v: campaigns.filter(c => c.status === 'draft').length, icon: 'fa-pen', color: 'secondary' },
            ].map((s, i) => (
              <div key={s.l} className="card-interactive p-4 animate-slide-up" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="flex items-center gap-2 mb-2"><i className={`fa-solid ${s.icon} text-${s.color} text-xs`} /><span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">{s.l}</span></div>
                <p className="font-heading text-2xl font-bold text-text-primary tabular-nums">{s.v}</p>
              </div>
            ))}
          </div>
          {campaigns.length === 0 ? (
            <div className="card-interactive py-20 text-center"><i className="fa-solid fa-bullhorn text-4xl text-text-muted opacity-20 mb-4 block" /><p className="text-text-muted font-medium">No email campaigns yet</p></div>
          ) : (
            <div className="space-y-3">
              {campaigns.map((c, i) => {
                const st = STATUS_LABELS[c.status] || STATUS_LABELS.draft;
                return (
                  <div key={c.id} className="card-interactive p-5 animate-slide-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`campaign-${c.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0"><i className="fa-solid fa-envelope text-primary text-lg" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2"><p className="text-sm font-semibold text-text-primary truncate">{c.name}</p><span className={`badge text-[10px] ${st.bg}`}><i className={`fa-solid ${st.icon} mr-1`} />{c.status}</span></div>
                          <p className="text-xs text-text-muted mt-0.5">Subject: {c.subject}</p>
                          <p className="text-[10px] text-text-muted mt-0.5">Targeting: {(c.target_statuses || []).join(', ')} · {c.target_count || 0} leads · {c.follow_up_count || 0} follow-ups every {c.follow_up_interval_days || 3}d</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="hidden sm:flex gap-4 mr-2">
                          <div><p className="text-xs text-text-muted">Sent</p><p className="text-sm font-bold tabular-nums">{c.sent_count || 0}</p></div>
                          <div><p className="text-xs text-text-muted">Target</p><p className="text-sm font-bold tabular-nums">{c.target_count || 0}</p></div>
                        </div>
                        {c.status === 'draft' && (
                          <button onClick={() => handleSend(c.id)} disabled={sending === c.id} className="btn-primary text-xs py-1.5 px-3" data-testid={`send-campaign-${c.id}`}>
                            {sending === c.id ? <i className="fa-solid fa-spinner fa-spin" /> : <><i className="fa-solid fa-paper-plane mr-1" />Send</>}
                          </button>
                        )}
                        <button onClick={() => viewDetail(c.id)} className="btn-secondary text-xs py-1.5 px-3"><i className="fa-solid fa-eye mr-1" />View</button>
                        <button onClick={() => handleDelete(c.id)} className="p-1.5 rounded-lg text-text-muted hover:text-destructive hover:bg-destructive-light transition-all"><i className="fa-solid fa-trash text-xs" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── SMS TAB ── */}
      {activeTab === 'sms' && (
        <>
          <div className="p-4 rounded-xl border border-green-200 bg-green-50 flex items-start gap-3" data-testid="sms-banner">
            <i className="fa-solid fa-comment-sms text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800">Twilio SMS — 98% Open Rate</p>
              <p className="text-xs text-green-700 mt-0.5">Twilio credentials active. Add <code className="bg-green-100 px-1 rounded font-mono">TWILIO_PHONE_NUMBER</code> to backend <code>.env</code> for live SMS delivery.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { l: 'SMS Campaigns', v: smsCampaigns.length, icon: 'fa-comment-sms' },
              { l: 'SMS Sent', v: smsCampaigns.reduce((a, c) => a + (c.sent_count || 0), 0), icon: 'fa-paper-plane' },
              { l: 'Active', v: smsCampaigns.filter(c => c.status !== 'draft').length, icon: 'fa-circle-check' },
              { l: 'Drafts', v: smsCampaigns.filter(c => c.status === 'draft').length, icon: 'fa-pen' },
            ].map((s, i) => (
              <div key={s.l} className="card-interactive p-4 animate-slide-up" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="flex items-center gap-2 mb-2"><i className={`fa-solid ${s.icon} text-green-600 text-xs`} /><span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">{s.l}</span></div>
                <p className="font-heading text-2xl font-bold text-text-primary tabular-nums">{s.v}</p>
              </div>
            ))}
          </div>

          {smsCampaigns.length === 0 ? (
            <div className="card-interactive py-20 text-center"><i className="fa-solid fa-comment-sms text-4xl text-text-muted opacity-20 mb-4 block" /><p className="text-text-muted font-medium">No SMS campaigns yet</p><p className="text-xs text-text-muted mt-1">SMS has 98% open rate — create your first campaign</p></div>
          ) : (
            <div className="space-y-3">
              {smsCampaigns.map((c, i) => (
                <div key={c.id} className="card-interactive p-5 animate-slide-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`sms-campaign-${c.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-12 h-12 rounded-xl bg-green-50 border border-green-200 flex items-center justify-center flex-shrink-0"><i className="fa-solid fa-comment-sms text-green-600 text-lg" /></div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-text-primary">{c.name}</p>
                          <span className={`badge text-[10px] ${c.status === 'simulated' ? 'bg-blue-50 text-blue-700' : c.status === 'sent' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>{c.status}</span>
                        </div>
                        <p className="text-xs text-text-muted mt-0.5 truncate max-w-sm">{c.message}</p>
                        <p className="text-[10px] text-text-muted mt-0.5">{c.target_count || 0} targets · {c.sent_count || 0} sent · {c.follow_up_count || 0} follow-ups</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {c.status === 'draft' && (
                        <button onClick={() => handleSmsSend(c.id)} disabled={sending === c.id + '_sms'} className="text-xs py-1.5 px-3 rounded-xl border font-semibold transition-all bg-green-600 text-white hover:bg-green-700 border-green-600" data-testid={`send-sms-${c.id}`}>
                          {sending === c.id + '_sms' ? <i className="fa-solid fa-spinner fa-spin" /> : <><i className="fa-solid fa-comment-sms mr-1" />Send SMS</>}
                        </button>
                      )}
                      <button onClick={() => handleSmsDelete(c.id)} className="p-1.5 rounded-lg text-text-muted hover:text-destructive hover:bg-destructive-light transition-all"><i className="fa-solid fa-trash text-xs" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create Email Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-surface border border-border rounded-2xl shadow-card-hover p-7 w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-slide-up relative" onClick={e => e.stopPropagation()} data-testid="create-campaign-modal">
            <button onClick={() => setShowCreate(false)} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-surface-alt"><i className="fa-solid fa-xmark text-text-muted" /></button>
            <h2 className="font-heading text-xl font-bold text-text-primary mb-1">Create Email Campaign</h2>
            <p className="text-sm text-text-muted mb-5">Automated outreach with drip follow-ups</p>
            <form onSubmit={handleCreate} className="space-y-4">
              <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Campaign Name</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="input-field h-9 text-sm" placeholder="Q1 Solar Outreach" /></div>
              <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Email Subject</label><input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} required className="input-field h-9 text-sm" placeholder="Save 70% on electricity, {{name}}" /></div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Email Body (HTML)</label>
                <textarea value={form.body} onChange={e => setForm({...form, body: e.target.value})} required rows={5} className="input-field h-auto py-2.5 resize-none text-sm font-mono" placeholder="<p>Hi {{name}},</p><p>Your home in {{city}} qualifies for solar...</p>" />
                <div className="flex gap-2 mt-1.5">{EMAIL_VARS.map(v => (<button key={v} type="button" onClick={() => setForm({...form, body: form.body + v})} className="text-[10px] px-2 py-0.5 rounded bg-primary/10 text-primary font-mono hover:bg-primary/20 transition-all">{v}</button>))}</div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Target Statuses</label>
                <div className="flex flex-wrap gap-2">
                  {['new', 'contacted', 'qualified', 'proposal_sent', 'lost'].map(st => (
                    <button key={st} type="button" onClick={() => toggleStatus(st)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${form.target_statuses.includes(st) ? 'border-primary bg-primary/5 text-primary' : 'border-border text-text-muted'}`}>{st.replace(/_/g, ' ')}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Follow-ups</label><input type="number" min={0} max={5} value={form.follow_up_count} onChange={e => setForm({...form, follow_up_count: parseInt(e.target.value)||0})} className="input-field h-9 text-sm" /></div>
                <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Interval (days)</label><input type="number" min={1} max={30} value={form.follow_up_interval_days} onChange={e => setForm({...form, follow_up_interval_days: parseInt(e.target.value)||3})} className="input-field h-9 text-sm" /></div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary text-sm">Cancel</button>
                <button type="submit" className="btn-primary text-sm" data-testid="save-campaign-btn"><i className="fa-solid fa-save mr-1.5" />Create Campaign</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create SMS Modal */}
      {showSmsCreate && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowSmsCreate(false)}>
          <div className="bg-surface border border-border rounded-2xl shadow-card-hover p-7 w-full max-w-lg max-h-[90vh] overflow-y-auto animate-slide-up relative" onClick={e => e.stopPropagation()} data-testid="create-sms-modal">
            <button onClick={() => setShowSmsCreate(false)} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-surface-alt"><i className="fa-solid fa-xmark text-text-muted" /></button>
            <h2 className="font-heading text-xl font-bold text-text-primary mb-1">Create SMS Campaign</h2>
            <p className="text-sm text-text-muted mb-5">Send texts via Twilio — 98% open rate</p>
            <form onSubmit={handleSmsCreate} className="space-y-4">
              <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Campaign Name</label><input value={smsForm.name} onChange={e => setSmsForm({...smsForm, name: e.target.value})} required className="input-field h-9 text-sm" placeholder="Spring Solar Blast" /></div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">SMS Message</label>
                <textarea value={smsForm.message} onChange={e => setSmsForm({...smsForm, message: e.target.value})} required rows={4} maxLength={320} className="input-field h-auto py-2.5 resize-none text-sm" placeholder="Hi {{name}}! Your home in {{city}} qualifies for $0-down solar. Reply YES for free quote or STOP to opt out." />
                <div className="flex items-center justify-between mt-1.5">
                  <div className="flex gap-2">{['{​{name}}','{{city}}','{{address}}'].map(v => (<button key={v} type="button" onClick={() => setSmsForm({...smsForm, message: smsForm.message + v})} className="text-[10px] px-2 py-0.5 rounded bg-green-50 text-green-700 font-mono hover:bg-green-100 transition-all">{v}</button>))}</div>
                  <span className={`text-[10px] font-mono ${smsForm.message.length > 160 ? 'text-orange-500' : 'text-text-muted'}`}>{smsForm.message.length}/320</span>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Target Statuses</label>
                <div className="flex flex-wrap gap-2">
                  {['new', 'contacted', 'qualified', 'proposal_sent', 'lost'].map(st => (
                    <button key={st} type="button" onClick={() => toggleSmsStatus(st)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${smsForm.target_statuses.includes(st) ? 'border-green-600 bg-green-50 text-green-700' : 'border-border text-text-muted'}`}>{st.replace(/_/g, ' ')}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Min Score</label><input type="number" min={0} max={100} value={smsForm.target_min_score} onChange={e => setSmsForm({...smsForm, target_min_score: parseInt(e.target.value)||0})} className="input-field h-9 text-sm" /></div>
                <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Follow-ups</label><input type="number" min={0} max={5} value={smsForm.follow_up_count} onChange={e => setSmsForm({...smsForm, follow_up_count: parseInt(e.target.value)||0})} className="input-field h-9 text-sm" /></div>
                <div><label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Days apart</label><input type="number" min={1} max={30} value={smsForm.follow_up_interval_days} onChange={e => setSmsForm({...smsForm, follow_up_interval_days: parseInt(e.target.value)||3})} className="input-field h-9 text-sm" /></div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowSmsCreate(false)} className="btn-secondary text-sm">Cancel</button>
                <button type="submit" className="text-sm py-2.5 px-5 rounded-xl font-semibold transition-all bg-green-600 text-white hover:bg-green-700" data-testid="save-sms-btn"><i className="fa-solid fa-comment-sms mr-1.5" />Create SMS Campaign</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-surface border border-border rounded-2xl shadow-card-hover p-7 w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-slide-up relative" onClick={e => e.stopPropagation()} data-testid="campaign-detail-modal">
            <button onClick={() => setDetail(null)} className="absolute top-4 right-4 p-2 rounded-lg hover:bg-surface-alt"><i className="fa-solid fa-xmark text-text-muted" /></button>
            <h2 className="font-heading text-xl font-bold text-text-primary mb-1">{detail.name}</h2>
            <p className="text-sm text-text-muted mb-4">Subject: {detail.subject}</p>
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[{ l: 'Sent', v: detail.sent_count || 0 }, { l: 'Target', v: detail.target_count || 0 }, { l: 'Status', v: detail.status }].map(s => (
                <div key={s.l} className="p-3 rounded-xl bg-surface-alt text-center"><p className="text-lg font-bold text-text-primary capitalize">{s.v}</p><p className="text-[10px] text-text-muted">{s.l}</p></div>
              ))}
            </div>
            {detail.sends && detail.sends.length > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Recipients</p>
                <div className="max-h-48 overflow-y-auto rounded-xl border border-border divide-y divide-border/50">
                  {detail.sends.map((s, i) => (
                    <div key={i} className="px-4 py-2.5 flex items-center justify-between">
                      <div><p className="text-sm font-medium text-text-primary">{s.lead_name}</p><p className="text-xs text-text-muted">{s.lead_email}</p></div>
                      <span className={`text-[10px] font-semibold ${s.status === 'sent' ? 'text-primary' : 'text-blue-500'}`}>{s.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
